import React, { useState } from 'react';
import { DishTask, Restaurant, Employee } from '../types';
import { 
  Users, 
  TrendingUp, 
  Award, 
  AlertTriangle, 
  CheckCircle2, 
  Clock, 
  ChevronRight, 
  HelpCircle, 
  Sparkles, 
  SlidersHorizontal, 
  Search, 
  ArrowRightLeft, 
  UserCheck, 
  Camera, 
  Layers, 
  UploadCloud, 
  CheckSquare,
  Activity,
  Zap,
  RotateCcw
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface TeamWorkloadViewProps {
  tasks: DishTask[];
  restaurants: Restaurant[];
  employees: Employee[];
  onUpdateTask: (id: string, updatedFields: Partial<DishTask>) => Promise<void>;
}

export const TeamWorkloadView: React.FC<TeamWorkloadViewProps> = ({
  tasks,
  restaurants,
  employees,
  onUpdateTask
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLoadStatus, setSelectedLoadStatus] = useState<'all' | 'overloaded' | 'optimal' | 'idle'>('all');
  const [activeTab, setActiveTab] = useState<'all' | 'outstanding' | 'by-task'>('all');
  const [reassigningStage, setReassigningStage] = useState<{
    taskId: string;
    stageId: 'photoStatus' | 'burstStatus' | 'aiVideoStatus' | 'uploadStatus';
    currentExec: string;
  } | null>(null);
  const [reassignTargetExec, setReassignTargetExec] = useState<string>('');
  const [reassigningInProgress, setReassigningInProgress] = useState(false);
  const [showToast, setShowToast] = useState<string | null>(null);

  const triggerToast = (msg: string) => {
    setShowToast(msg);
    setTimeout(() => setShowToast(null), 3000);
  };

  // Safe names for all team members (ensure consistent naming list)
  const allCrewNames = Array.from(new Set([
    ...employees.map(e => e.name),
    ...tasks.flatMap(t => [t.executor, t.photoExecutor, t.burstExecutor, t.aiVideoExecutor, t.uploadExecutor]),
    'Anar Əliyev',
    'Rəşad Məmmədov',
    'Aysel Rzayeva',
    'Leyla Həsənova',
    'Fərid Bağırov',
    'Günel İsmayılova'
  ])).filter(Boolean) as string[];

  // Helper to extract accumulated time tracking logs (cached in localStorage by worker screen)
  const getAccumulatedTime = (employeeName: string) => {
    try {
      const saved = localStorage.getItem('employee_time_logs');
      if (!saved) return 0;
      const logs = JSON.parse(saved);
      let total = 0;
      Object.entries(logs).forEach(([key, value]) => {
        if (key.startsWith(employeeName) && typeof value === 'number') {
          total += value;
        }
      });
      return total;
    } catch {
      return 0;
    }
  };

  const formatDuration = (sec: number) => {
    if (sec <= 0) return '0 d';
    const hours = Math.floor(sec / 3600);
    const minutes = Math.floor((sec % 3600) / 60);
    if (hours > 0) return `${hours}saat ${minutes}dəq`;
    return `${minutes} dəqiqə`;
  };

  // Compile detailed workload and efficiency metrics for each team member
  const memberWorkloadStats = allCrewNames.map(name => {
    // Collect stages where this member is responsible
    const assignedPhoto = tasks.filter(t => (t.photoExecutor || t.executor) === name);
    const assignedBurst = tasks.filter(t => (t.burstExecutor || t.executor) === name);
    const assignedAi = tasks.filter(t => (t.aiVideoExecutor || t.executor) === name);
    const assignedUpload = tasks.filter(t => (t.uploadExecutor || t.executor) === name);

    const totalStages = 
      assignedPhoto.length + 
      assignedBurst.length + 
      assignedAi.length + 
      assignedUpload.length;

    const completedStages = 
      assignedPhoto.filter(t => t.photoStatus === 'completed').length +
      assignedBurst.filter(t => t.burstStatus === 'completed').length +
      assignedAi.filter(t => t.aiVideoStatus === 'completed').length +
      assignedUpload.filter(t => t.uploadStatus === 'completed').length;

    const inProgressStages = 
      assignedPhoto.filter(t => t.photoStatus === 'in_progress').length +
      assignedBurst.filter(t => t.burstStatus === 'in_progress').length +
      assignedAi.filter(t => t.aiVideoStatus === 'in_progress').length +
      assignedUpload.filter(t => t.uploadStatus === 'in_progress').length;

    const pendingStages = totalStages - (completedStages + inProgressStages);

    // Completion percentage rate
    const completionRate = totalStages > 0 ? Math.round((completedStages / totalStages) * 100) : 0;

    // Workload state threshold (Trello/ClickUp style congestion rating)
    // - Overloaded: Active (pending + in_progress) >= 5 stages
    // - Optimal: Active between 2 and 4 stages
    // - Idle: Active < 2 stages
    const activeLoadCount = pendingStages + inProgressStages;
    let loadState: 'overloaded' | 'optimal' | 'idle' = 'idle';
    if (activeLoadCount >= 5) {
      loadState = 'overloaded';
    } else if (activeLoadCount >= 2) {
      loadState = 'optimal';
    }

    // Bind employee object properties for visuals
    const matchedEmployeeObj = employees.find(e => e.name === name);
    const avatar = matchedEmployeeObj?.avatar || name.split(' ').map(n => n[0]).join('');
    const bg = matchedEmployeeObj?.bg || 'bg-slate-500';
    const role = matchedEmployeeObj?.role || 'Kreativ istehsalat üzvü';

    // Track statistics for different stages
    const stagesDetail = {
      photo: { total: assignedPhoto.length, completed: assignedPhoto.filter(t => t.photoStatus === 'completed').length },
      burst: { total: assignedBurst.length, completed: assignedBurst.filter(t => t.burstStatus === 'completed').length },
      aiVideo: { total: assignedAi.length, completed: assignedAi.filter(t => t.aiVideoStatus === 'completed').length },
      upload: { total: assignedUpload.length, completed: assignedUpload.filter(t => t.uploadStatus === 'completed').length }
    };

    return {
      name,
      avatar,
      bg,
      role,
      totalStages,
      completedStages,
      inProgressStages,
      pendingStages,
      activeLoadCount,
      completionRate,
      loadState,
      stagesDetail,
      trackedTime: getAccumulatedTime(name)
    };
  });

  // Filter and Rank list
  const filteredWorkloadStats = memberWorkloadStats.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          item.role.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLoad = selectedLoadStatus === 'all' || item.loadState === selectedLoadStatus;
    return matchesSearch && matchesLoad;
  });

  // Sort based on Leaderboard efficiency metrics (highest completion rate, and secondarily total completed items)
  const rankedLeaderboard = [...memberWorkloadStats]
    .filter(item => item.totalStages > 0)
    .sort((a, b) => b.completionRate - a.completionRate || b.completedStages - a.completedStages);

  // General counters
  const totalInProduction = tasks.length;
  const globalCompletedCount = tasks.filter(t => t.status === 'completed').length;
  const overallKPI = totalInProduction > 0 ? Math.round((globalCompletedCount / totalInProduction) * 100) : 0;
  
  const overloadedCount = memberWorkloadStats.filter(e => e.loadState === 'overloaded').length;
  const optimalCount = memberWorkloadStats.filter(e => e.loadState === 'optimal').length;
  const idleCount = memberWorkloadStats.filter(e => e.loadState === 'idle').length;

  // Handle reassigning workflow stage task
  const executeReassignment = async () => {
    if (!reassigningStage || !reassignTargetExec) return;
    setReassigningInProgress(true);
    try {
      const { taskId, stageId, currentExec } = reassigningStage;
      
      // Determine what field of database to modify
      let updateObj: Partial<DishTask> = {};
      if (stageId === 'photoStatus') {
        updateObj.photoExecutor = reassignTargetExec;
      } else if (stageId === 'burstStatus') {
        updateObj.burstExecutor = reassignTargetExec;
      } else if (stageId === 'aiVideoStatus') {
        updateObj.aiVideoExecutor = reassignTargetExec;
      } else {
        updateObj.uploadExecutor = reassignTargetExec;
      }

      await onUpdateTask(taskId, updateObj);
      triggerToast(`🔄 İstehsalat yükü rebalans edildi! "${stageId === 'photoStatus' ? 'Foto 9:16' : stageId === 'burstStatus' ? 'Laylama' : stageId === 'aiVideoStatus' ? 'Sİ Video' : 'QR Yükləmə'}" mərhələsi ${currentExec} kadrından ${reassignTargetExec} kadrına keçirildi.`);
      setReassigningStage(null);
      setReassignTargetExec('');
    } catch (e) {
      console.error(e);
      triggerToast('⚠️ Əməliyyat alınmadı. Network xətası');
    } finally {
      setReassigningInProgress(false);
    }
  };

  return (
    <div className="space-y-6 text-slate-800 animate-fade-in" id="team-workload-container">
      
      {/* Dynamic Native Toast */}
      {showToast && (
        <div className="fixed bottom-6 right-6 z-[99] bg-slate-900 text-white px-5 py-3.5 rounded-2xl shadow-xl flex items-center gap-2.5 text-xs font-bold leading-none border border-slate-850 animate-bounce">
          <Zap className="w-3.5 h-3.5 text-indigo-400 animate-pulse fill-indigo-400" />
          <span>{showToast}</span>
        </div>
      )}

      {/* Screen Title Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <span className="text-[10px] uppercase font-mono bg-indigo-50 text-indigo-700 font-extrabold px-3 py-1 rounded-full border border-indigo-120">
            Baku Menu CRM // İdarəetmə Alətləri
          </span>
          <h2 className="text-xl md:text-2xl font-black text-slate-950 flex items-center gap-2 mt-2">
            👨‍💻 Komanda Yüklənməsi və Effektivlik Reytinqi
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Çəkiliş, montaj, süni intellekt generativ video istehsalı və yüklənmə dərəcələrinin real zaman rejimində spektri.
          </p>
        </div>
      </div>

      {/* Grid of General Team Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" id="team-kpi-metrics">
        
        {/* Metric 1 */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-2 select-none">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-mono text-slate-400 font-bold uppercase tracking-wider">Aktiv Istehsalat KPI</span>
            <div className="p-1.5 rounded-lg bg-indigo-50 text-indigo-700">
              <TrendingUp className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-black tracking-tight text-slate-900">{overallKPI}%</p>
          <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
            <span>Bitmiş tapşırıq: {globalCompletedCount} / {totalInProduction}</span>
          </div>
        </div>

        {/* Metric 2 */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-2 select-none">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-mono text-slate-400 font-bold uppercase tracking-wider">Gərgin Kadrlar</span>
            <div className="p-1.5 rounded-lg bg-rose-50 text-rose-700 border border-rose-100">
              <AlertTriangle className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-black tracking-tight text-rose-600">{overloadedCount} nəfər</p>
          <p className="text-xs font-semibold text-slate-500 leading-none">
            {overloadedCount > 0 ? '⚠️ Yük rebalansı tələb olunur!' : '✅ Bütün kadrlar optimal rejimdədir.'}
          </p>
        </div>

        {/* Metric 3 */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-2 select-none">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-mono text-slate-400 font-bold uppercase tracking-wider">Optimal Yük</span>
            <div className="p-1.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-100">
              <CheckCircle2 className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-black tracking-tight text-slate-900">{optimalCount} nəfər</p>
          <p className="text-xs font-medium text-slate-400 leading-none">
            2 ilə 4 arasında aktiv vəzifə
          </p>
        </div>

        {/* Metric 4 */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-2 select-none">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-mono text-slate-400 font-bold uppercase tracking-wider">Mövcud Resurslar</span>
            <div className="p-1.5 rounded-lg bg-slate-50 text-slate-500 border border-slate-200">
              <Users className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-black tracking-tight text-slate-900">{idleCount} nəfər</p>
          <p className="text-xs font-semibold text-indigo-650 leading-none">
            Yüksək sürətli yeni sifariş qəbulu
          </p>
        </div>

      </div>

      {/* LEADERBOARD & RATING SECTION */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Column 1: Outstanding Performance Leaderborad (Left) */}
        <div className="lg:col-span-1 bg-gradient-to-br from-slate-900 to-slate-950 text-white rounded-2xl p-6 border border-slate-800 shadow-lg space-y-5">
          <div className="select-none flex items-center justify-between border-b border-white/10 pb-4">
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500 animate-pulse fill-amber-500" />
              <h3 className="text-sm font-extrabold tracking-tight">Qızıl Reytinq (KPI)</h3>
            </div>
            <span className="text-[9px] font-mono tracking-widest text-slate-450 uppercase">İllik Turnir</span>
          </div>

          {rankedLeaderboard.length === 0 ? (
            <p className="text-xs italic text-slate-400 text-center py-8">Hazırda statistik məlumat tapılmadı.</p>
          ) : (
            <div className="space-y-4">
              {rankedLeaderboard.slice(0, 5).map((stat, idx) => {
                const medal = idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : '🎯';
                return (
                  <div key={stat.name} className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                    <div className="flex items-center gap-3">
                      <span className="text-lg select-none">{medal}</span>
                      <div className="space-y-0.5">
                        <h4 className="text-xs font-extrabold tracking-tight">{stat.name}</h4>
                        <p className="text-[9px] text-slate-400 font-sans font-medium">{stat.role}</p>
                      </div>
                    </div>

                    <div className="text-right">
                      <span className="text-xs font-black font-mono text-emerald-400 block">{stat.completionRate}%</span>
                      <span className="text-[9px] text-slate-400 font-mono select-none font-bold">bitib: {stat.completedStages}</span>
                    </div>
                  </div>
                );
              })}

              <div className="p-3.5 bg-indigo-500/10 border border-indigo-500/25 rounded-xl text-left">
                <h5 className="text-[10px] uppercase font-mono tracking-wider font-extrabold text-indigo-400">📊 KPI Necə Hesablanır?</h5>
                <p className="text-[10px] text-slate-300 leading-relaxed mt-1">
                  Kadrın üstünlük dərəcəsi onun öhdəsinə qoyulan 4 növ media tapşırıqları (Fotosessiya, Burst həlli, Süni İntellekt mühərriki və bulud sistemi) üzrə bitirdiyi balların cəmindən ibarətdir.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Column 2 & 3: Workload Spectrums & Capacity Chart (Right) */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6 shadow-xs flex flex-col justify-between">
          
          <div className="space-y-4">
            {/* Header and filters inside the sheet */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-150">
              <div className="flex items-center gap-2 select-none">
                <Users className="w-4 h-4 text-indigo-650" />
                <h3 className="text-sm font-bold text-slate-900">Kadr Yüklənməsi & Doluluq Xəritəsi</h3>
              </div>

              {/* Workload Status filter pills */}
              <div className="flex items-center gap-1.5 overflow-x-auto select-none">
                <button
                  onClick={() => setSelectedLoadStatus('all')}
                  className={`px-3 py-1 rounded-lg text-[10px] font-black tracking-wide bg-slate-100 hover:bg-slate-200 text-slate-600 transition-all cursor-pointer ${selectedLoadStatus === 'all' ? 'bg-slate-700 text-white hover:bg-slate-805' : ''}`}
                >
                  Hamısı
                </button>
                <button
                  onClick={() => setSelectedLoadStatus('overloaded')}
                  className={`px-3 py-1 rounded-lg text-[10px] font-black tracking-wide bg-rose-50 hover:bg-rose-100 text-rose-700 transition-all cursor-pointer ${selectedLoadStatus === 'overloaded' ? 'bg-rose-600 text-white hover:bg-rose-700' : ''}`}
                >
                  Gərgin ({overloadedCount})
                </button>
                <button
                  onClick={() => setSelectedLoadStatus('optimal')}
                  className={`px-3 py-1 rounded-lg text-[10px] font-black tracking-wide bg-emerald-50 hover:bg-emerald-100 text-emerald-700 transition-all cursor-pointer ${selectedLoadStatus === 'optimal' ? 'bg-emerald-600 text-white hover:bg-emerald-700' : ''}`}
                >
                  Optimal ({optimalCount})
                </button>
                <button
                  onClick={() => setSelectedLoadStatus('idle')}
                  className={`px-3 py-1 rounded-lg text-[10px] font-black tracking-wide bg-slate-50 hover:bg-slate-200 text-slate-505 transition-all cursor-pointer ${selectedLoadStatus === 'idle' ? 'bg-indigo-600 text-white hover:bg-indigo-700' : ''}`}
                >
                  Yüksək resurs ({idleCount})
                </button>
              </div>
            </div>

            {/* Searching Tool */}
            <div className="relative">
              <Search className="absolute left-3.5 top-2.5 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="İstənilən komanda üzvü və ya vəzifəsini axtarın..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-xs border border-slate-200 rounded-xl focus:border-indigo-500 bg-slate-50/50 focus:bg-white text-slate-800 font-medium"
              />
            </div>

            {/* List of workload meters */}
            <div className="space-y-4 max-h-[400px] overflow-y-auto pr-1">
              {filteredWorkloadStats.map(stat => {
                const isOver = stat.loadState === 'overloaded';
                const isOpt = stat.loadState === 'optimal';
                const pillColor = isOver 
                  ? 'bg-rose-50 border-rose-200 text-rose-700' 
                  : isOpt 
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700' 
                  : 'bg-slate-50 border-slate-200 text-slate-600';

                return (
                  <div key={stat.name} className="p-4 rounded-xl border border-slate-100 bg-slate-50/40 hover:bg-slate-50/80 transition-all space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-xl ${stat.bg} text-white flex items-center justify-center font-black text-xs select-none shadow-sm`}>
                          {stat.avatar}
                        </div>
                        <div>
                          <h4 className="text-xs font-extrabold text-slate-900 leading-none">{stat.name}</h4>
                          <p className="text-[10px] text-slate-400 font-semibold mt-1">{stat.role}</p>
                        </div>
                      </div>

                      {/* Pill Status */}
                      <div className="flex items-center gap-2 select-none self-start sm:self-auto">
                        <span className={`px-2.5 py-0.5 text-[10px] rounded-full border font-bold font-mono tracking-wide uppercase ${pillColor}`}>
                          {isOver ? '⚠️ Gərgin yüklənmə' : isOpt ? '✅ Optimal yük' : '🍃 Boş resurs'}
                        </span>
                        
                        {stat.trackedTime > 0 && (
                          <span className="px-2 py-0.5 bg-amber-50 border border-amber-200 text-amber-800 text-[10px] rounded-full font-mono font-bold">
                            ⏱️ {formatDuration(stat.trackedTime)}
                          </span>
                        )}
                        
                        <span className="text-xs font-black font-mono text-indigo-750 px-2.5 py-0.5 bg-indigo-50 border border-indigo-120 rounded-full">
                          {stat.activeLoadCount} aktiv növbə
                        </span>
                      </div>
                    </div>

                    {/* Progress details of subtasks */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px] font-mono font-bold text-slate-500">
                      <div className="p-2 bg-white rounded-lg border border-slate-100 flex items-center justify-between">
                        <span className="flex items-center gap-1">
                          <Camera className="w-3 h-3 text-indigo-500" />
                          Foto 9:16
                        </span>
                        <span className="text-slate-800">{stat.stagesDetail.photo.completed} / {stat.stagesDetail.photo.total}</span>
                      </div>

                      <div className="p-2 bg-white rounded-lg border border-slate-100 flex items-center justify-between">
                        <span className="flex items-center gap-1">
                          <Layers className="w-3 h-3 text-indigo-500" />
                          Laylama
                        </span>
                        <span className="text-slate-800">{stat.stagesDetail.burst.completed} / {stat.stagesDetail.burst.total}</span>
                      </div>

                      <div className="p-2 bg-white rounded-lg border border-slate-100 flex items-center justify-between">
                        <span className="flex items-center gap-1">
                          <Sparkles className="w-3 h-3 text-indigo-500" />
                          Sİ Video
                        </span>
                        <span className="text-slate-800">{stat.stagesDetail.aiVideo.completed} / {stat.stagesDetail.aiVideo.total}</span>
                      </div>

                      <div className="p-2 bg-white rounded-lg border border-slate-100 flex items-center justify-between">
                        <span className="flex items-center gap-1">
                          <UploadCloud className="w-3 h-3 text-indigo-500" />
                          Menyu QR
                        </span>
                        <span className="text-slate-800">{stat.stagesDetail.upload.completed} / {stat.stagesDetail.upload.total}</span>
                      </div>
                    </div>

                    {/* KPI overall bar */}
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-[10px] select-none text-slate-450 font-semibold font-mono">
                        <span>Ümumi istehsalat konveyeri kəmiyyəti</span>
                        <span className="font-extrabold text-indigo-650">{stat.completionRate}% bitmə səviyyəsi</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                        <div 
                          className="bg-indigo-600 h-1.5 rounded-full transition-all duration-300"
                          style={{ width: `${stat.completionRate}%` }}
                        />
                      </div>
                    </div>

                  </div>
                );
              })}
            </div>
          </div>

          <div className="pt-4 border-t border-slate-150 select-none text-left text-[11px] text-slate-400 italic">
            💡 Tövsiyə: Yüklənməsi 5-dən çox olan əməkdaşların üzərindəki bəzi iş mərhələlərini digər boş kadrlara ötürərək konveyeri sürətləndirə bilərsiniz.
          </div>

        </div>

      </div>

      {/* DETAILED WORKLOAD REBALANCING BOARD (ClickUp / Bitrix level) */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-5">
        
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 select-none pb-4 border-b border-slate-150">
          <div className="space-y-1 text-left">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <ArrowRightLeft className="w-4 h-4 text-indigo-600 animate-pulse" />
              <span>Dinamik Yük Rebalansı və Reassigner Aləti</span>
            </h3>
            <p className="text-[11px] text-slate-400">
              Aşağıdakı yemək tapşırıqlarında gecikmə yaranmaması üçün hər hansı bir məhsulun alt pilləsini dərhal başqasına həvalə edin.
            </p>
          </div>
          
          <span className="px-3 py-1 bg-yellow-50 border border-yellow-200 text-yellow-800 text-[10px] font-black font-mono rounded-lg">
            ⚡ Bitrix24 Smart Integration
          </span>
        </div>

        {/* Action Panel for Reassignment */}
        <AnimatePresence>
          {reassigningStage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="p-5 bg-indigo-50 border border-indigo-200 rounded-2xl flex flex-col md:flex-row md:items-center justify-between gap-4 text-left"
            >
              <div className="space-y-1">
                <span className="text-[9px] font-mono text-indigo-600 uppercase font-extrabold tracking-widest block">Növbə Sürüşdürülməsi Aktivdir</span>
                <p className="text-xs text-slate-750">
                  Siz <b>{tasks.find(t => t.id === reassigningStage.taskId)?.dishName}</b> yeməyinin 
                  <b> "{reassigningStage.stageId === 'photoStatus' ? 'Foto 9:16' : 
                          reassigningStage.stageId === 'burstStatus' ? 'Laylama' : 
                          reassigningStage.stageId === 'aiVideoStatus' ? 'Sİ Video' : 'QR Yükləmə'}"</b> mərhələsini 
                  <b> {reassigningStage.currentExec}</b> kadrından kimə ötürmək istəyirsiniz?
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3 shrink-0">
                <select
                  value={reassignTargetExec}
                  onChange={e => setReassignTargetExec(e.target.value)}
                  className="px-3 py-2 border border-slate-200 focus:border-indigo-400 rounded-xl text-xs bg-white text-slate-700 font-bold outline-hidden transition-all cursor-pointer"
                >
                  <option value="">-- Yeni məsul şəxs seç --</option>
                  {allCrewNames.filter(name => name !== reassigningStage.currentExec).map(name => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setReassigningStage(null)}
                    disabled={reassigningInProgress}
                    className="px-3 py-2 bg-white hover:bg-slate-100 border border-slate-200 text-slate-600 font-bold text-xs rounded-xl cursor-pointer transition-all"
                  >
                    İmtina
                  </button>
                  <button
                    onClick={executeReassignment}
                    disabled={!reassignTargetExec || reassigningInProgress}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-xs cursor-pointer transition-all flex items-center gap-1.5"
                  >
                    {reassigningInProgress ? 'Köçürülür...' : 'Həvalə et'}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Task workflow list table for rebalancing */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[800px]">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] text-slate-450 font-mono font-bold uppercase tracking-wider select-none">
                <th className="py-3 px-4">Yemək və Restoran adı</th>
                <th className="py-3 px-4 text-center">📸 Foto 9:16 icraçı</th>
                <th className="py-3 px-4 text-center">🥞 Laylama icraçı</th>
                <th className="py-3 px-4 text-center">🎬 Sİ Video icraçı</th>
                <th className="py-3 px-4 text-center">☁️ QR yüklə icraçı</th>
                <th className="py-3 px-4 text-right">Düymə</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs text-left">
              {tasks.slice(0, 15).map(task => {
                const photoE = task.photoExecutor || task.executor;
                const burstE = task.burstExecutor || task.executor;
                const aiE = task.aiVideoExecutor || task.executor;
                const uploadE = task.uploadExecutor || task.executor;

                return (
                  <tr key={task.id} className="hover:bg-slate-50/50 transition-colors">
                    
                    {/* Dish */}
                    <td className="py-3 px-4">
                      <div className="space-y-0.5">
                        <span className="font-extrabold text-slate-900 leading-tight block">{task.dishName}</span>
                        <span className="text-[10px] text-slate-450 font-mono font-bold">🏢 {task.restaurantName}</span>
                      </div>
                    </td>

                    {/* Foto status and assignee */}
                    <td className="py-3 px-4">
                      <div className="flex flex-col items-center justify-center gap-1">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${
                          task.photoStatus === 'completed' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' :
                          task.photoStatus === 'in_progress' ? 'bg-amber-50 text-amber-705 border border-amber-100' : 'bg-slate-50 text-slate-505 border border-slate-150'
                        }`}>
                          {photoE}
                        </span>
                        <button
                          onClick={() => setReassigningStage({ taskId: task.id, stageId: 'photoStatus', currentExec: photoE })}
                          className="text-[9px] text-indigo-500 hover:text-indigo-700 hover:underline font-semibold cursor-pointer select-none"
                        >
                          Reassign
                        </button>
                      </div>
                    </td>

                    {/* Burst */}
                    <td className="py-3 px-4">
                      <div className="flex flex-col items-center justify-center gap-1">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${
                          task.burstStatus === 'completed' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' :
                          task.burstStatus === 'in_progress' ? 'bg-amber-50 text-amber-705 border border-amber-100' : 'bg-slate-50 text-slate-505 border border-slate-150'
                        }`}>
                          {burstE}
                        </span>
                        <button
                          onClick={() => setReassigningStage({ taskId: task.id, stageId: 'burstStatus', currentExec: burstE })}
                          className="text-[9px] text-indigo-500 hover:text-indigo-700 hover:underline font-semibold cursor-pointer select-none"
                        >
                          Reassign
                        </button>
                      </div>
                    </td>

                    {/* AI Video */}
                    <td className="py-3 px-4">
                      <div className="flex flex-col items-center justify-center gap-1">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${
                          task.aiVideoStatus === 'completed' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' :
                          task.aiVideoStatus === 'in_progress' ? 'bg-amber-50 text-amber-705 border border-amber-100' : 'bg-slate-50 text-slate-505 border border-slate-150'
                        }`}>
                          {aiE}
                        </span>
                        <button
                          onClick={() => setReassigningStage({ taskId: task.id, stageId: 'aiVideoStatus', currentExec: aiE })}
                          className="text-[9px] text-indigo-500 hover:text-indigo-700 hover:underline font-semibold cursor-pointer select-none"
                        >
                          Reassign
                        </button>
                      </div>
                    </td>

                    {/* Upload */}
                    <td className="py-3 px-4">
                      <div className="flex flex-col items-center justify-center gap-1">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${
                          task.uploadStatus === 'completed' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' :
                          task.uploadStatus === 'in_progress' ? 'bg-amber-50 text-amber-705 border border-amber-100' : 'bg-slate-50 text-slate-505 border border-slate-150'
                        }`}>
                          {uploadE}
                        </span>
                        <button
                          onClick={() => setReassigningStage({ taskId: task.id, stageId: 'uploadStatus', currentExec: uploadE })}
                          className="text-[9px] text-indigo-500 hover:text-indigo-700 hover:underline font-semibold cursor-pointer select-none"
                        >
                          Reassign
                        </button>
                      </div>
                    </td>

                    {/* Overall indicator */}
                    <td className="py-3 px-4 text-right">
                      <span className={`px-2 py-0.5 rounded-md font-mono text-[10px] font-bold ${
                        task.status === 'completed' ? 'bg-emerald-600 text-white' :
                        task.status === 'in_progress' ? 'bg-amber-500 text-white' : 'bg-slate-300 text-slate-800'
                      }`}>
                        {task.status === 'completed' ? 'Bitib' : task.status === 'in_progress' ? 'İcrada' : 'Növbədə'}
                      </span>
                    </td>

                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

      </div>

    </div>
  );
};
